import React, { useState, useMemo } from 'react';
import { formatExeclNormalPercent, formatExeclNum, formatNum, formatPercentNum, getChartDateFormatString } from '@/pages/dashboard/utils/utils';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import { connect, getLocale } from 'umi';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import { cloneDeep } from 'lodash-es';
import { addChartLabel } from '@/pages/dashboard/components/Chart/_config/plug';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import ChurnChartCo from '../Nightingale/component/ChurnChartCo';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';

const WeekChurnUserCo = (props: any) => {
  const locale = getLocale();
  const { getWeekDay } = useWeekDay();
  const isEN = locale === 'en-US';
  const { config: propsConfig, data: apiData, explainInfo, filterVal, apiDataKey } = props;
  const { timeType } = filterVal;
  const { metricsKey, title: titleEn, titleCn, toolTip, chart, tab, titleDes: titleDesConfig, dataKey, defaultActive = 1, hideConfig, table, otherConfigFunc, height } = propsConfig[timeType] || {};

  const [tabName, setTabName] = useState('');
  const [isEye, setIsEye] = useState(true);

  const title = isEN ? titleEn : titleCn;
  const toolTipTitle = isEN ? toolTip?.title : toolTip?.titleCn || '';
  const toolTipMetricsName = isEN ? toolTip?.metrics : toolTip?.metricsCn || '';

  const chartConfig = useMemo(()=> chart?.map((v)=> ({
    ...v,
    title: isEN ? v.title : v.titleCn,
  })),[isEN, chart]);

  const data = useMemo(()=> {
    const metricsData = apiData?.[apiDataKey] || {};
    let isLoading = true;
    let isError = false;
    let isNoData = false;
    let componentData : any = [];
    if(metricsData){
      componentData = metricsKey ? metricsData?.data?.[metricsKey] : metricsData?.data || [];
      isLoading = metricsData?.loading;
      isError = metricsData?.errorMsg;
      isNoData = componentData?.length <= 0;
    }
    return {
      componentData,
      isLoading,
      isError,
      isNoData
    };
  },[apiData, apiDataKey, metricsKey]);

  const tabList = tab?.list?.map((v: { text: string; textCn: string; value: string | number; }) => ({
    text: isEN ? v.text : v.textCn,
    value: v.value
  }));
  
  const titleDes = titleDesConfig?.map((val)=> {
    const explain = getExplainVal(explainInfo, val?.rainbowKey, getLocale(), val?.explainEn, val?.explainCn);
    const content = isEN ? val.contentEn : val.contentCn;

    if(!explain && !content){
      return null;
    }

    return {
      title: isEN ? val.title : val.titleCn,
      content: val.isRainbow ? explain : content
    };
  }).filter((val : { title: string; content: string; } | null) => val !== null);

  const typeKeyMap = chart?.reduce((acc, val) => {
    const title = isEN ? val.title : val.titleCn;
    acc[title] = val.dataType;
    return acc;
  }, {});

  // 折线图 && 表格
  const options = useMemo(() => {
    const dataList: Array<listItem> = data.componentData || [];
    const accChart = chartConfig?.reduce((pre, { title, type, key }) => {
      if (dataList.length) {
        const chartData = {
          name: title,
          type,
          barCategoryGap: '50%',
          data: dataList.map((item) => ({
            x: item.date,
            y: item?.[key] || '',
          })),
        };
        pre.push(chartData);
      }
      return pre;
    }, [] as any[]);

    const chart = accChart;

    const tableOption = table?.map((val)=> ({
      key: val.key,
      align: val.align,
      fixed: val.fixed || '',
      width: val.width,
      header: isEN ? val.title : val.titleCn,
      render: (cvm) => {
        if(val.key === 'date'){
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{cvm?.date}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        }
        return <span>{cvm[val.key]}</span>;
      },
    })) || [];

    const tableData = dataList?.map((item) => {
      const formattedItem : any = {};
      table?.forEach(each => {
        if(each.dataType === 'date'){
          formattedItem[each.key] = getChartDateFormatString(timeType, item[each.key], true);
        }else if(each.dataType === 'percent'){
          formattedItem[each.key] = formatPercentNum(parseFloat(item[each.key] || ''), 2);
        }else{
          formattedItem[each.key] = formatNum(item[each.key] || '', false, true);
        }
      });
      return formattedItem;
    }) || [];

    const tableDownloadData = dataList?.map((item) => {
      const formattedItem : any = {};
      table?.forEach(each => {
        if(each.dataType === 'date'){
          formattedItem[each.key] = getChartDateFormatString(timeType, item[each.key], true);
        }else if(each.dataType === 'percent'){
          formattedItem[each.key] = formatExeclNormalPercent(parseFloat(item[each.key] || ''));
        }else{
          formattedItem[each.key] = formatExeclNum(item[each.key] || '');
        }
      });
      return formattedItem;
    }) || [];

    return {
      chart,
      table: {
        option: tableOption,
        data: tableData,
        downloadData: tableDownloadData,
      },
    };
  }, [data.componentData, dataKey, chartConfig, tabName, timeType]);

  const axisLabelFormat = (opt: any, timeType: any) => opt.xAxis?.map((item: any) => ({
    ...item,
    axisLabel: {
      ...item.axisLabel,
      formatter: (value: any) => getChartDateFormatString(timeType, value),
    },
  }));

  const setOtherConfigFunc = (opt: any, timeType: any) => {
    const { y1: y1Config, y2: y2Config } = otherConfigFunc || {};

    if(timeType === 'Month'){
      opt.xAxis = axisLabelFormat(opt, timeType);
      const y1 = opt.yAxis;
      const y2 = cloneDeep(opt.yAxis);
      y1.alignTicks = true;
     
      if(y1Config?.type === 'percent') {
        y1.axisLabel.formatter = (value: any) => (value <= 1 && value !== 0 ? formatPercentNum(value) : value);
      }
      opt.yAxis = [y1];
      if(y2Config?.show === true){
        y2.alignTicks = true;
        y2.axisLabel.formatter = (v) => !!v && y2Config?.type === 'percent' ? formatPercentNum(parseFloat(v), v < 0.01 ? 2 : 0) : formatNum(v, false, true) || v;
        opt.yAxis = [y1, y2];
        opt.series.forEach((d, i) => { d.yAxisIndex = i });
      }
      
      return addChartLabel(opt, {
        formatter: (params: { value: string | number; }) => {
          const result = y1Config?.type === 'percent' ? formatPercentNum(parseFloat(params?.value), 2) : formatNum(params?.value, false, true);
          return result;
        },
        show: isEye,
        formatNum: (value: string | number) => y1Config?.type === 'percent' ? formatPercentNum(parseFloat(value), 2) : formatNum(value, false, true)
      });
    }

    const y1 = opt.yAxis;
    const y2 = cloneDeep(opt.yAxis);
    y2.alignTicks = true;
    y2.axisLabel.formatter = (value: any) => (value && `${formatPercentNum(value, value < 0.01 ? 2 : 0)}`) || value;

    opt.xAxis = axisLabelFormat(opt, timeType);
    opt.yAxis = [y1, y2];
    opt.series.forEach((y: { yAxisIndex: any; }, i: any) => {
      y.yAxisIndex = i;
    });
    return addChartLabel(opt, {
      formatter: (params: { value: string | number; }) => formatNum(params?.value, false, true),
      show: isEye,
      formatNum: (value: string | number) => formatNum(value, false, true)
    });
  };

  const formatterFunc = (params: any) => getEchartTooltipText({
    title: toolTipTitle || dateTimeFormat(`${params[0].axisValue}`, timeType === 'Week' ? 'YYYY-MM-DD' : 'YYYY-MM'),
    metricsName: toolTipMetricsName || '',
    data: params.map(({ seriesName, value, color }: any) => ({
      name: seriesName,
      metricsNum: typeKeyMap[seriesName] === 'percent' ? formatPercentNum(parseFloat(value), 2) : formatNum(value, false, true),
      color,
    })),
  });

  return (
    <>
      <ChurnChartCo
        options={options}
        tabName={tabName}
        setTabName={setTabName}
        timeType={timeType}
        tabList={tabList}
        title={title}
        titleDes={titleDes}
        isLoading={data.isLoading}
        isError={data.isError}
        isNoData={data.isNoData}
        defaultActive={defaultActive}
        formatterFunc={formatterFunc}
        setOtherConfigFunc={setOtherConfigFunc}
        isEye={isEye}
        setIsEye={setIsEye}
        hideConfig={hideConfig}
        height={height}
      />
    </>
  );
};


export default connect((state: any) => ({
  explainInfo: state.dashboard?.explainInfo,
}))(WeekChurnUserCo);
